/** Deploy-target switch for the Strategy Detail header (paper trading,
 * 2026-07-10).
 *
 * A strategy is deployed either LIVE (orders reach the broker, real money)
 * or PAPER (the full lifecycle — entry, SL, exit, P&L, logs, reports — runs
 * on synthetic fills, no broker). Renders the current mode as a pill;
 * clicking opens a confirmation and PATCHes /api/strategies/{id}. Applies to
 * every strategy type (unlike ModeSwitch, which is bias-only). */

import { useState, type ReactElement } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { strategiesApi } from "@/lib/api";
import type { ExecutionMode, Strategy } from "@/types/strategy";
import { STRATEGIES_KEY } from "./useStrategyActions";

export function ExecutionModeSwitch({ strategy }: Readonly<{ strategy: Strategy }>): ReactElement {
  const qc = useQueryClient();
  const [pending, setPending] = useState<ExecutionMode | null>(null);
  const mut = useMutation({
    mutationFn: (execution_mode: ExecutionMode) =>
      strategiesApi.update(strategy.id, { execution_mode }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STRATEGIES_KEY });
      setPending(null);
    },
    onError: () => {
      setPending(null);
    },
  });

  const paper = strategy.execution_mode === "PAPER";
  const next: ExecutionMode = paper ? "LIVE" : "PAPER";
  return (
    <>
      <button
        type="button"
        onClick={() => {
          setPending(next);
        }}
        className={`pill hover:opacity-80 ${
          paper ? "bg-warning-bg text-warning" : "bg-success-bg text-success"
        }`}
        aria-label="Switch deploy mode"
      >
        <i className={`ti ${paper ? "ti-flask" : "ti-rocket"} text-sm`} />
        {paper ? "Paper" : "Live"}
        <i className="ti ti-switch-horizontal text-xs" />
      </button>
      <ExecutionModeConfirm
        pending={pending}
        busy={mut.isPending}
        onCancel={() => {
          setPending(null);
        }}
        onConfirm={() => {
          if (pending !== null) mut.mutate(pending);
        }}
      />
    </>
  );
}

const LABEL: Record<ExecutionMode, string> = { LIVE: "Live", PAPER: "Paper" };
const BLURB: Record<ExecutionMode, string> = {
  PAPER:
    "New trades run on SYNTHETIC fills — no order reaches the broker. Entry, SL, exit, P&L, logs and reports all work as normal, kept separate from your live numbers. Any already-open live trade keeps running for real.",
  LIVE: "New trades place REAL broker orders with real money. Any open paper trade keeps running on paper until it closes.",
};

interface ConfirmProps {
  pending: ExecutionMode | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function ExecutionModeConfirm({
  pending,
  busy,
  onCancel,
  onConfirm,
}: Readonly<ConfirmProps>): ReactElement {
  const label = pending !== null ? LABEL[pending] : "";
  const goingLive = pending === "LIVE";
  return (
    <ConfirmDialog
      open={pending !== null}
      title={pending !== null ? `Deploy ${label}?` : ""}
      description={pending !== null ? BLURB[pending] : ""}
      confirmLabel={`Deploy ${label}`}
      icon={goingLive ? "ti-rocket" : "ti-flask"}
      tone={goingLive ? "danger" : "warning"}
      busy={busy}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}
